import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX, Loader2 } from "lucide-react";

/**
 * LiveVideo — cinematic, self-managing video surface.
 * • Poster-first; the <video> is only given a src when it nears the viewport (IO).
 * • Autoplays MUTED once ~40% visible; pauses when scrolled away (saves CPU).
 * • Desktop: pointer enter → unmute (respecting autoplay policy); leave → mute.
 * • Touch: tap toggles audio; playback keeps running.
 * • Never bypasses browser autoplay security; if play() rejects, poster stays.
 * • Respects prefers-reduced-motion (no autoplay; poster + manual tap to play).
 */
export function LiveVideo({ item, className = "", rounded = "rounded-2xl", showMeta = true, priority = false, testid }) {
  const prm = useReducedMotion();
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const [near, setNear] = useState(priority);   // src attached
  const [playing, setPlaying] = useState(false);
  const [audible, setAudible] = useState(false);
  const [ready, setReady] = useState(false);
  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  // attach src + autoplay when near viewport; pause when away
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setNear(true);
          const v = vidRef.current;
          if (v && !prm) v.play?.().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
          const v = vidRef.current;
          if (v) { v.pause?.(); setPlaying(false); setAudible(false); if (v) v.muted = true; }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [prm]);

  const enableAudio = useCallback((on) => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = !on;
    if (on && v.paused) v.play?.().catch(() => {});
    setAudible(on);
  }, []);

  const onEnter = () => { if (!isTouch && !prm) enableAudio(true); };
  const onLeave = () => { if (!isTouch) enableAudio(false); };
  const onTap = () => {
    const v = vidRef.current;
    if (!v) return;
    if (prm && v.paused) { v.play?.().then(() => setPlaying(true)).catch(() => {}); return; }
    enableAudio(!audible);
  };

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={isTouch ? onTap : undefined}
      className={`group relative overflow-hidden ${rounded} bg-[#0a0b13] ${className}`}
    >
      {/* poster underlay — always present, fades once video paints */}
      <img
        src={item.poster} alt={item.title}
        loading={priority ? "eager" : "lazy"} decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: playing && ready ? 0 : 1, transition: "opacity .6s ease" }}
      />
      {near && (
        <video
          ref={vidRef}
          src={item.src}
          poster={item.poster}
          muted loop playsInline preload="metadata"
          autoPlay={!prm}
          onCanPlay={() => setReady(true)}
          onPlaying={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* cinematic frame: hairline key light + vignette (glass language) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-[10%] right-[10%] h-px bg-[linear-gradient(90deg,transparent,rgba(124,112,255,0.55),transparent)] opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(130%_100%_at_50%_0%,transparent_55%,rgba(6,7,13,0.5)_100%)]" />
      </div>

      {/* loading shimmer before first frame */}
      {near && !ready && !prm && (
        <div className="absolute inset-0 grid place-items-center">
          <Loader2 className="w-6 h-6 text-white/50 animate-spin" />
        </div>
      )}

      {/* audio state chip (desktop) / tap hint (touch) */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
           style={{ opacity: (audible || isTouch) ? 1 : undefined }}>
        <span className="grid place-items-center w-8 h-8 rounded-full bg-black/55 border border-white/15 backdrop-blur text-white">
          {audible ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </span>
      </div>

      {/* elegant metadata — rises on hover, never a giant play button */}
      {showMeta && (
        <motion.div
          initial={false}
          className="absolute inset-x-0 bottom-0 p-4 sm:p-5 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
          <div className="relative translate-y-1.5 group-hover:translate-y-0 transition-transform duration-500">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#c9c1ff] px-2 py-0.5 rounded-md bg-black/50 border border-white/10 backdrop-blur">{item.tag}</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">{item.label}</span>
            </div>
            <div className="font-display text-lg sm:text-xl font-semibold text-white mt-2" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>{item.title}</div>
            <div className="text-[12px] sm:text-sm text-slate-300/90 mt-0.5 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 leading-snug">{item.blurb}</div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
